<template>
    <Spinner v-if="!isDoneLoading" />
    <div class="title-and-controls">
        <div class="page-title">
            <span class="page-title">
                <h1>{{ processName }}</h1>
            </span>
        </div>
        <div class="controls">
            <div
                id="properties-panel"
                ref="propertiesPanelElement"
                :class="{
                    'visible': definition?.content && isEditing,
                }"
            />
            <Button
                v-for="button in buttons"
                :key="button.label"
                :class="button.class"
                :icon="button.icon"
                :label="button.label"
                @click="button.onClick"
            />
            <template v-if="definition?.content">
                <Button
                    severity="secondary"
                    icon="pi pi-ellipsis-v"
                    ref="optionsButtonElement"
                    aria-haspopup="true"
                    aria-controls="process_popup_menu"
                    aria-label="Document Menu"
                    @click="toggleMenu"
                />
                <Menu
                    id="process_popup_menu"
                    ref="menu"
                    :model="contextMenuItems"
                    :popup="true"
                />
                <input
                    name="file"
                    type="file"
                    accept=".bpmn"
                    style="display: none"
                    ref="fileUploadElement"
                    @change="
                        (e) => uploadProcessDefinition(e as HTMLInputEvent)
                    "
                />
            </template>
        </div>
    </div>
    <div class="definition" v-if="isDoneLoading">
        <template v-if="isViewingHistory">
            <template v-if="comparisonArtefact?.content">
                <ProcessDefinitionDiff
                    v-if="selectedArtefact?.content"
                    :from="comparisonArtefact.content"
                    :to="selectedArtefact.content"
                    ref="historicalDefinitionElement"
                    :showAsDiff="route.query.compare !== undefined"
                />
            </template>
            <!-- When there are no older artefacts, we can't show a meaningful
                diff yet. -->
            <template v-else>
                <ProcessDefinition
                    v-if="selectedArtefact?.content"
                    v-model:bpmnArtefact="selectedArtefact as BpmnArtefact"
                    :isMaximizingEnabled="false"
                    ref="historicalDefinitionElement"
                />
            </template>
        </template>
        <template v-else>
            <ProcessDefinition
                v-if="definition?.content"
                v-model:bpmnArtefact="definition"
                :isMaximizingEnabled="false"
                ref="definitionElement"
            />
            <ProcessDefinitionCreator
                v-else-if="definition && process"
                v-model="definition"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import ProcessDefinitionDiff from "@/components/Diff/ProcessDefinitionDiff.vue";
import ProcessDefinition from "@/components/Process/Definition.vue";
import ProcessDefinitionCreator from "@/components/Process/DefinitionCreator.vue";
import {
    useProcessManager,
    useProcessStore,
} from "@/composables/process-manager";
import Spinner from "@/shared/Spinner.vue";
import { downloadBlob } from "@/utils";
import Menu from "primevue/menu";
import { useConfirm } from "primevue/useconfirm";
import { computed, ref } from "vue";

import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { useGenerateBpmnDialog } from "@/composables/generate-bpmn-dialog";
import { BpmnArtefact } from "@/lib/artefacts";
import { useArtefactsStore } from "@/lib/artefacts/user";
import { useHistoryManager, useHistoryStore } from "@/stores/history";
import { useRolesManager } from "@/stores/roles-store";
import { useTaskManager } from "@/stores/task-store";
import Button, { ButtonProps } from "primevue/button";
import type { MenuItem } from "primevue/menuitem";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";

const menu = ref<InstanceType<typeof Menu>>();
const propertiesPanelElement = ref<HTMLElement>();
const { updateProcess } = useProcessStore();
const { process, definition, isFetching } = useProcessManager();
const { roles } = useRolesManager();
const artefactsStore = useArtefactsStore();

const { startEditing } = useEditStore();
const { isEditing } = useEditManager();
const isRestoring = ref(false);
const router = useRouter();
const route = useRoute();

const { loadHistory } = useHistoryStore();
const {
    isViewingLatestVersion,
    isViewingHistory,
    selectedArtefact: selectedHistoricalArtefact,
    comparisonArtefact,
    isFetchingHistory,
    isFetchingHistoricalArtefact,
} = useHistoryManager();
const { open: openGenerateDialog } = useGenerateBpmnDialog({
    roles,
    artefact: definition,
});

const selectedArtefact = computed(() => {
    if (isViewingHistory.value) {
        return selectedHistoricalArtefact.value;
    }
    return definition.value;
});

const fileUploadElement = ref<HTMLElement>();

/**
 * Check if the given filelist contains exactly one BPMN file.
 */
const isBpmnFile = (files?: FileList | null) => {
    if (!files || files.length !== 1) {
        return false;
    }
    return files[0].name.endsWith(".bpmn");
};

const uploadProcessDefinition = async (event: HTMLInputEvent) => {
    if (!event.target.files || !isBpmnFile(event.target.files)) {
        return;
    }
    await definition.value?.updateContent(event.target.files[0]);
    await definition.value?.persist();
    startEditing();
};

const isDoneLoading = computed(() => {
    return (
        !isFetching.value &&
        !isFetchingHistory.value &&
        !isFetchingHistoricalArtefact.value &&
        !isRestoring.value
    );
});

useTaskManager();

const toggleMenu = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    menu.value?.toggle(event);
};

const confirm = useConfirm();

const processName = computed(() => {
    if (!process.value) {
        return "...";
    }

    return process.value.name;
});

const downloadAsBpmn = () => {
    if (process.value && selectedArtefact.value) {
        downloadBlob(
            URL.createObjectURL(selectedArtefact.value?.blob),
            `${process.value?.name}.bpmn`
        );
    }
};

const definitionElement = ref<InstanceType<typeof ProcessDefinition>>();
const historicalDefinitionElement =
    ref<InstanceType<typeof ProcessDefinition>>();
const selectedDefinitionElement = computed(() => {
    return isViewingHistory.value
        ? historicalDefinitionElement.value
        : definitionElement.value;
});

const downloadAsSvg = async () => {
    if (!selectedDefinitionElement.value || !process.value) {
        return;
    }
    const svg = await selectedDefinitionElement.value.getImage("svg");
    if (!svg) {
        return;
    }

    downloadBlob(URL.createObjectURL(svg), `${process.value.name}.svg`);
};

const downloadAsPng = async () => {
    if (!selectedDefinitionElement.value || !process.value) {
        return;
    }

    const png = await selectedDefinitionElement.value.getImage("png");
    if (!png) {
        return;
    }

    downloadBlob(URL.createObjectURL(png), `${process.value.name}.png`);
};

onBeforeRouteLeave(async () => {
    await definitionElement.value?.completeActiveActions();
});

const restoreConfirmation = async () => {
    return new Promise((resolve) => {
        confirm.require({
            message: "Are you sure you want restore this version?",
            header: "Restore version",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
                label: "Cancel",
                severity: "secondary",
                outlined: true,
            },
            acceptProps: {
                label: "Restore",
            },
            accept: () => {
                resolve(true);
            },
            reject: () => {
                resolve(false);
            },
            onHide: () => {
                resolve(false);
            },
        });
    });
};

const buttonsAndTheirConditions = computed(() => [
    {
        class: "p-button-secondary",
        icon: "pi pi-check",
        label: "Restore",
        click: async () => {
            const shouldRestore = await restoreConfirmation();
            if (!shouldRestore) {
                return;
            }

            isRestoring.value = true;
            try {
                await artefactsStore.restoreArtefact(selectedArtefact.value!);
                await Promise.all([updateProcess(true), loadHistory()]);
                await router.push({
                    name: route.name,
                    params: {
                        ...route.params,
                        version: "current",
                    },
                    query: route.query,
                });
            } finally {
                isRestoring.value = false;
            }
        },
        condition: () => !isViewingLatestVersion.value,
    },
]);

const buttons = computed<ButtonProps[]>(() => {
    return buttonsAndTheirConditions.value
        .filter((button) => !button.condition || button.condition())
        .map((button) => {
            return {
                class: button.class,
                icon: button.icon,
                label: button.label,
                onClick: button.click,
            };
        });
});

const contextMenuItemsAndTheirConditions = computed(() => [
    {
        label: "Download as SVG",
        icon: "pi pi-image",
        command: downloadAsSvg,
    },
    {
        label: "Download as PNG",
        icon: "pi pi-image",
        command: downloadAsPng,
    },
    {
        label: "Download as BPMN",
        icon: "pi pi-download",
        command: downloadAsBpmn,
    },
    {
        label: "Upload a BPMN diagram",
        icon: "pi pi-upload",
        command: () => fileUploadElement.value?.click(),
        condition: () => isViewingLatestVersion.value,
    },
    {
        label: "Regenerate process",
        icon: "pi pi-sparkles",
        command: () => openGenerateDialog(),
        condition: () => isViewingLatestVersion.value,
    },
    {
        label: "History",
        icon: "pi pi-history",
        command: () => {
            router.push({
                name: route.name,
                params: {
                    ...route.params,
                    version: "current",
                },
            });
        },
        condition: () => !isViewingHistory.value && !isEditing.value,
    },
]);

const contextMenuItems = computed<MenuItem[]>(() => {
    return contextMenuItemsAndTheirConditions.value
        .filter((item) => !item.condition || item.condition())
        .map((item) => {
            return {
                label: item.label,
                icon: item.icon,
                command: item.command,
            };
        });
});
</script>

<style scoped lang="scss">
.page-title {
    margin-left: 1rem;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 0;
}

.definition {
    flex: 1;
    display: flex;
}
</style>
